import { useEffect, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { FieldEmpty, FieldPanelHeading, FieldStatus } from './field-primitives';
import { FieldTicketForm } from './field-ticket-form';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';
import './field-discovery.css';

import type { FieldPanelProps } from './field-types';
import type { FieldDestination } from '../field-overview';
import type { FieldActorId, FieldTicket } from '@mio/shared';

const columns: { status: FieldTicket['status']; label: string }[] = [
  { status: 'backlog', label: 'Backlog' },
  { status: 'in_progress', label: 'In progress' },
  { status: 'in_review', label: 'In review' },
  { status: 'done', label: 'Done' },
];

export function FieldBoard({
  snapshot,
  busy,
  onAction,
  onOpenMeeting,
  focusId,
  focusRequest,
  onNavigate,
}: FieldPanelProps & {
  onOpenMeeting?: (meetingId: string) => void;
  focusId?: string | undefined;
  focusRequest?: number;
  onNavigate?: (destination: FieldDestination) => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [postingStandup, setPostingStandup] = useState(false);
  const [standup, setStandup] = useState('');
  const [standupSent, setStandupSent] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [participants, setParticipants] = useState<FieldActorId[]>(['jordan']);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [dayFilter, setDayFilter] = useState(snapshot.discovery ? 'all' : 'current');
  const [statusFilter, setStatusFilter] = useState('all');
  const [savedTicket, setSavedTicket] = useState<string | null>(null);
  const [editingTicket, setEditingTicket] = useState<string | null>(null);
  const [deferTicket, setDeferTicket] = useState<string | null>(null);
  const [deferReason, setDeferReason] = useState('');
  const statuses = snapshot.discovery
    ? [...columns, { status: 'deferred' as const, label: 'Deferred' }]
    : columns;
  const selectedTicket = snapshot.tickets.find((ticket) => ticket.id === editingTicket);
  useEffect(() => {
    if (!focusId) return;
    setSearch('');
    setDayFilter('all');
    setStatusFilter('all');
    const frame = requestAnimationFrame(() => {
      const element = Array.from(
        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
      ).find((element) => element.dataset.objectId === focusId);
      const detail = element?.querySelector('details');
      if (detail) detail.open = true;
      element?.focus({ preventScroll: true });
      element?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusId, focusRequest]);
  const day = snapshot.engagement.day;
  const tickets = snapshot.tickets
    .filter((ticket) => {
      const owner =
        snapshot.actors.find((actor) => actor.id === ticket.owner)?.name ?? ticket.owner;
      const matchesDay =
        dayFilter === 'all' ||
        (dayFilter === 'current'
          ? ticket.day === day || ticket.status === 'in_progress' || ticket.status === 'in_review'
          : ticket.day === Number(dayFilter));
      return (
        matchesDay &&
        (statusFilter === 'all' || ticket.status === statusFilter) &&
        `${ticket.title} ${ticket.id} ${ticket.description} ${owner}`
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      );
    })
    .sort((a, b) =>
      snapshot.discovery
        ? { high: 0, normal: 1, low: 2 }[a.priority ?? 'normal'] -
          { high: 0, normal: 1, low: 2 }[b.priority ?? 'normal']
        : 0,
    );
  return (
    <div ref={panel} className="field-object-panel h-full overflow-y-auto">
      <FieldPanelHeading
        title="Tickets"
        tourTarget="tickets"
        action={
          <div className="flex flex-wrap gap-2">
            {snapshot.discovery ? (
              <Button
                size="sm"
                disabled={busy || !snapshot.enabled}
                aria-expanded={editingTicket === 'new'}
                onClick={() => setEditingTicket(editingTicket === 'new' ? null : 'new')}
              >
                New ticket
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPostingStandup(!postingStandup)}
              aria-expanded={postingStandup}
            >
              Post standup
            </Button>
          </div>
        }
      >
        {snapshot.discovery
          ? 'Your delivery plan. Define useful increments, prioritize the work, and link it to agreed outcomes.'
          : 'Today’s work and the tickets already in progress.'}
      </FieldPanelHeading>
      {snapshot.discovery && editingTicket ? (
        <FieldTicketForm
          key={editingTicket}
          snapshot={snapshot}
          busy={busy}
          onAction={onAction}
          {...(selectedTicket ? { ticket: selectedTicket } : {})}
          onClose={() => setEditingTicket(null)}
        />
      ) : null}
      <section hidden={!postingStandup} className="field-object-form">
        <h3 className="text-base font-semibold">Post your daily standup</h3>
        <form
          className="mt-4 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            setStandupSent(false);
            void onAction({ type: 'standup.post', text: standup }).then((sent) => {
              if (sent) {
                setStandup('');
                setStandupSent(true);
              }
            });
          }}
        >
          <label htmlFor="standup-text" className={FIELD_LABEL}>
            Done · Doing · Blocked (include ticket references)
          </label>
          <textarea
            id="standup-text"
            className={FIELD_INPUT}
            rows={4}
            required
            maxLength={8000}
            value={standup}
            onChange={(event) => setStandup(event.target.value)}
          />
          <p className="text-fg-muted text-xs">This update is addressed to all five colleagues.</p>
          <Button type="submit" size="sm" disabled={busy || !standup.trim()}>
            Post to the team
          </Button>
          {standupSent ? (
            <p role="status" className="text-success text-sm">
              Your standup was posted.
            </p>
          ) : null}
        </form>
      </section>
      <div className="field-object-toolbar">
        <div className="field-object-search">
          <label htmlFor="ticket-search" className={FIELD_LABEL}>
            Find a ticket
          </label>
          <input
            id="ticket-search"
            type="search"
            className={FIELD_INPUT}
            value={search}
            placeholder="Search title, owner, or ticket ID"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="ticket-day" className={FIELD_LABEL}>
            {snapshot.discovery ? 'Created' : 'Schedule'}
          </label>
          <SelectField
            id="ticket-day"
            value={dayFilter}
            onValueChange={setDayFilter}
            options={[
              { value: 'current', label: `Day ${day} & active work` },
              { value: 'all', label: snapshot.discovery ? 'All work' : 'All days' },
              ...Array.from(new Set(snapshot.tickets.map((ticket) => ticket.day)))
                .sort((a, b) => a - b)
                .map((ticketDay) => ({ value: String(ticketDay), label: `Day ${ticketDay}` })),
            ]}
          />
        </div>
        <div>
          <label htmlFor="ticket-filter-status" className={FIELD_LABEL}>
            Status
          </label>
          <SelectField
            id="ticket-filter-status"
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All statuses' },
              ...statuses.map((column) => ({ value: column.status, label: column.label })),
            ]}
          />
        </div>
      </div>
      <div className="field-object-list-summary" aria-live="polite">
        <span>
          {tickets.length} of {snapshot.tickets.length} tickets
        </span>
        <span>{snapshot.tickets.filter((ticket) => ticket.status === 'done').length} complete</span>
      </div>
      {savedTicket ? (
        <p role="status" className="field-object-feedback">
          Status updated for {savedTicket}.
        </p>
      ) : null}
      {!snapshot.tickets.length ? (
        <FieldEmpty
          title={
            snapshot.discovery ? 'Shape your first increment' : 'No tickets have been assigned'
          }
        >
          {snapshot.discovery
            ? 'Explore the brief and starter, clarify the important decisions, then create the tickets you need. Your board is yours to organize.'
            : 'Your project board will appear here when the engagement is ready.'}
        </FieldEmpty>
      ) : !tickets.length ? (
        <FieldEmpty title="No tickets match these filters">
          Choose all days or try another title, owner, or ticket ID.
        </FieldEmpty>
      ) : (
        <div className="field-ticket-list">
          {tickets.map((ticket) => (
            <article
              key={ticket.id}
              tabIndex={-1}
              data-object-id={ticket.id}
              data-focused={focusId === ticket.id}
              className="field-ticket-row"
            >
              <details className="field-ticket-detail">
                <summary>
                  <span className="field-ticket-title">{ticket.title}</span>
                  <span className="field-object-meta">
                    <span>Day {ticket.day}</span>
                    {snapshot.discovery ? (
                      <span>
                        {ticket.priority === 'high'
                          ? 'High priority'
                          : ticket.priority === 'low'
                            ? 'Low priority'
                            : 'Normal priority'}
                      </span>
                    ) : null}
                    <span>
                      {snapshot.actors.find((actor) => actor.id === ticket.owner)?.name ??
                        ticket.owner}
                    </span>
                    <span className="field-object-id">{ticket.id}</span>
                  </span>
                </summary>
                <div className="field-ticket-body">
                  <p className="whitespace-pre-wrap text-sm leading-6">{ticket.description}</p>
                  {ticket.deferralReason ? (
                    <p className="text-fg-muted mt-3 text-sm leading-6">
                      Deferral reason: {ticket.deferralReason}
                    </p>
                  ) : null}
                  <h4 className="mt-4 text-sm font-semibold">Acceptance criteria</h4>
                  <ul className="text-fg-muted mt-2 list-disc space-y-1 pl-4 text-sm leading-6">
                    {ticket.acceptance.map((criterion) => (
                      <li key={criterion}>{criterion}</li>
                    ))}
                  </ul>
                  {ticket.dependencies.length ? (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold">Depends on</h4>
                      <ul className="text-fg-muted mt-2 list-disc space-y-1 pl-4 text-sm leading-6">
                        {ticket.dependencies.map((id) => (
                          <li key={id}>
                            {snapshot.tickets.find((item) => item.id === id)?.title ?? id}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {ticket.outcomeIds?.length ? (
                    <p className="text-fg-muted mt-4 text-xs leading-5">
                      Supports{' '}
                      {ticket.outcomeIds
                        .map(
                          (id) =>
                            snapshot.discovery?.outcomes.find((item) => item.id === id)?.title ??
                            id,
                        )
                        .join(' · ')}
                    </p>
                  ) : null}
                  {ticket.decisionIds?.length ? (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold">Related decisions</h4>
                      <ul className="field-discovery-evidence">
                        {ticket.decisionIds.map((id) => (
                          <li key={id}>
                            {onNavigate ? (
                              <button
                                type="button"
                                onClick={() => onNavigate({ section: 'engagement', focusId: id })}
                              >
                                {snapshot.discovery?.decisions.find((item) => item.id === id)
                                  ?.title ?? id}
                              </button>
                            ) : (
                              <span>
                                {snapshot.discovery?.decisions.find((item) => item.id === id)
                                  ?.title ?? id}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {ticket.findingId && onNavigate ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3"
                      onClick={() =>
                        onNavigate({ section: 'engagement', focusId: ticket.findingId })
                      }
                    >
                      View original finding
                    </Button>
                  ) : null}
                  {deferTicket === ticket.id ? (
                    <form
                      className="mt-4 space-y-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void onAction({
                          type: 'ticket.update',
                          ticketId: ticket.id,
                          status: 'deferred',
                          reason: deferReason,
                        }).then((saved) => {
                          if (saved) {
                            setDeferTicket(null);
                            setDeferReason('');
                            setSavedTicket(ticket.title);
                          }
                        });
                      }}
                    >
                      <label htmlFor={`defer-${ticket.id}`} className={FIELD_LABEL}>
                        Reason for deferral
                      </label>
                      <textarea
                        id={`defer-${ticket.id}`}
                        className={FIELD_INPUT}
                        rows={3}
                        maxLength={4000}
                        required
                        value={deferReason}
                        onChange={(event) => setDeferReason(event.target.value)}
                      />
                      <p className="text-fg-muted text-xs leading-5">
                        Deferring a ticket does not remove an agreed outcome. Confirm any scope
                        change with its owner first.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" type="submit" disabled={busy || !deferReason.trim()}>
                          Defer ticket
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={() => setDeferTicket(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : null}
                </div>
              </details>
              <div className="field-ticket-status">
                <label className="sr-only" htmlFor={`status-${ticket.id}`}>
                  Status for {ticket.title}
                </label>
                <SelectField
                  id={`status-${ticket.id}`}
                  disabled={busy || !snapshot.enabled}
                  value={ticket.status}
                  onValueChange={(nextValue) => {
                    const status = statuses.find((item) => item.status === nextValue)?.status;
                    if (status === 'deferred') {
                      setDeferTicket(ticket.id);
                      setDeferReason('');
                      const element = Array.from(
                        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
                      ).find((item) => item.dataset.objectId === ticket.id);
                      const detail = element?.querySelector('details');
                      if (detail) detail.open = true;
                      return;
                    }
                    if (status) {
                      setSavedTicket(null);
                      void onAction({ type: 'ticket.update', ticketId: ticket.id, status }).then(
                        (saved) => {
                          if (saved) setSavedTicket(ticket.title);
                        },
                      );
                    }
                  }}
                  options={statuses.map((item) => ({ value: item.status, label: item.label }))}
                />
                {snapshot.discovery ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Refine ${ticket.title}`}
                    onClick={() => {
                      setEditingTicket(ticket.id);
                      panel.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Refine ticket
                  </Button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
      <FieldPanelHeading title="Meetings & notes">
        Record the agenda, participants, and the decisions you reached. Calls and direct messages
        stay in Conversations; meeting rooms keep their own shared discussion.
      </FieldPanelHeading>
      <div className="space-y-4 p-5">
        {snapshot.meetings.map((meeting) => (
          <article key={meeting.id} className="border-line rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-medium">{meeting.title}</h3>
              <FieldStatus value={meeting.status} />
            </div>
            <p className="text-fg-muted mt-2 text-xs">
              {meeting.actorIds
                .map((id) => snapshot.actors.find((actor) => actor.id === id)?.name ?? id)
                .join(' · ')}
            </p>
            {onOpenMeeting ? (
              <Button size="sm" variant="secondary" onClick={() => onOpenMeeting(meeting.id)}>
                {meeting.status === 'active' ? 'Join meeting room' : 'View meeting room'}
              </Button>
            ) : null}
            {meeting.status === 'active' ? (
              <form
                className="mt-3 space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void onAction({
                    type: 'meeting.end',
                    meetingId: meeting.id,
                    notes: notes[meeting.id] ?? '',
                  });
                }}
              >
                <label htmlFor={`notes-${meeting.id}`} className={FIELD_LABEL}>
                  Meeting notes
                </label>
                <textarea
                  id={`notes-${meeting.id}`}
                  className={FIELD_INPUT}
                  rows={3}
                  maxLength={16000}
                  value={notes[meeting.id] ?? ''}
                  onChange={(event) =>
                    setNotes((previous) => ({ ...previous, [meeting.id]: event.target.value }))
                  }
                />
                <Button type="submit" variant="secondary" size="sm" disabled={busy}>
                  Save notes & end meeting
                </Button>
              </form>
            ) : (
              <p className="mt-3 whitespace-pre-wrap text-sm">
                {meeting.notes || 'No notes recorded.'}
              </p>
            )}
          </article>
        ))}
        <details className="border-line rounded-md border p-4">
          <summary className="cursor-pointer text-sm font-medium">Start a meeting</summary>
          <form
            className="mt-4 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              void onAction({
                type: 'meeting.start',
                title: meetingTitle,
                actorIds: participants,
              }).then((sent) => {
                if (sent) setMeetingTitle('');
              });
            }}
          >
            <label htmlFor="meeting-agenda" className={FIELD_LABEL}>
              Title or agenda
            </label>
            <input
              id="meeting-agenda"
              required
              maxLength={200}
              className={FIELD_INPUT}
              value={meetingTitle}
              onChange={(event) => setMeetingTitle(event.target.value)}
            />
            <fieldset>
              <legend className={FIELD_LABEL}>Participants</legend>
              <div className="flex flex-wrap gap-4">
                {snapshot.actors.map((actor) => (
                  <label key={actor.id} className="field-object-choice">
                    <input
                      type="checkbox"
                      checked={participants.includes(actor.id)}
                      onChange={(event) =>
                        setParticipants((current) =>
                          event.target.checked
                            ? [...current, actor.id]
                            : current.filter((id) => id !== actor.id),
                        )
                      }
                    />
                    {actor.name.split(' ')[0]}
                  </label>
                ))}
              </div>
            </fieldset>
            <Button
              disabled={busy || !meetingTitle.trim() || !participants.length}
              type="submit"
              size="sm"
            >
              Start meeting
            </Button>
          </form>
        </details>
      </div>
    </div>
  );
}
